import type { KeyboardEvent, ReactNode } from 'react';
import { Icon, type IconName } from '../ui/Icon';
import { Button, Input } from '../ui/primitives';
import { cx, type Tone } from '../ui/primitives/cx';
import { ProductHelp, ProductOption, ProductProblem, ProductSwitch, ProductTile } from './ProductParts';
import {
  COST_OPTIONS,
  PRODUCT_NAME_MAX,
  TAX_OPTIONS,
  filterRecipes,
  isStoreChosen,
  sanitizePercent,
  sanitizePrice,
  taxHint,
  type ProductDraft,
  type ProductStep,
  type RecipeChoice,
  type StoreOption,
  type TaxChoice,
} from '../../utils/newProduct';
import type { CostMode } from '../../types/otherSales';

// Rediseño · Bloque E — las cinco preguntas de P-029, una tarjeta cada una. Solo dibujan; el estado
// vive en `ProductWizard`. P-071: la cuarta pregunta pasó de «¿usa una receta?» a «¿Cómo quieres llevar
// el costo?», con tres respuestas; conserva el id de paso `recipe`.

/** El color y el dibujo de cada paso (P-029, `META`). El costo lleva el icono de recetas del
 *  producto, no el cono de la maqueta: es el dibujo de un negocio concreto. */
export const STEP_LOOK: Record<ProductStep, { icon: IconName; tone: Tone; label: string }> = {
  name: { icon: 'tag', tone: 'danger', label: 'Nombre' },
  price: { icon: 'cash', tone: 'success', label: 'Precio' },
  stores: { icon: 'stores', tone: 'info', label: 'Se vende en' },
  recipe: { icon: 'recipes', tone: 'warning', label: 'Costo' },
  tax: { icon: 'percent', tone: 'accent', label: 'Impuesto' },
};

export const STEP_QUESTION: Record<ProductStep, string> = {
  name: '¿Qué vas a vender?',
  price: '¿En cuánto lo vendes?',
  stores: '¿Dónde lo vendes?',
  recipe: '¿Cómo quieres llevar el costo?',
  tax: '¿Cómo manejas el impuesto?',
};

const TAX_LOOK: Record<TaxChoice, { icon: IconName; tone: Tone | 'neutral' }> = {
  NONE: { icon: 'void', tone: 'neutral' },
  INCLUDED: { icon: 'tag-check', tone: 'success' },
  CUSTOM: { icon: 'percent', tone: 'accent' },
};

const COST_LOOK: Record<CostMode, { icon: IconName; tone: Tone | 'neutral' }> = {
  NONE: { icon: 'bag', tone: 'neutral' },
  APPROXIMATE: { icon: 'cash', tone: 'info' },
  RECIPE: { icon: 'recipes', tone: 'warning' },
};

export type RecipesState = { status: 'loading' } | { status: 'error' } | { status: 'ready'; choices: RecipeChoice[] };

export interface StepProblem { place: 'question' | 'sub'; message: string }

interface StepCardProps {
  step: ProductStep;
  draft: ProductDraft;
  problem: StepProblem | null;
  disabled: boolean;
  onDraft: (patch: Partial<ProductDraft>) => void;
  onToggleAll: () => void;
  onToggleStore: (storeId: string) => void;
  onEnter: () => void;
  // Tiendas
  ownerAll: boolean;
  stores: readonly StoreOption[];
  // Receta
  recipes: RecipesState;
  recipeQuery: string;
  onRecipeQuery: (query: string) => void;
  onRetryRecipes: () => void;
}

/** La pregunta del paso actual. */
export function StepCard(props: StepCardProps) {
  const { step, problem } = props;
  const questionId = `product-q-${step}`;
  const helpId = `product-help-${step}`;
  const problemId = `product-problem-${step}`;
  const questionProblem = problem?.place === 'question' ? problem.message : null;
  const subProblem = problem?.place === 'sub' ? problem.message : null;
  const describedBy = [questionProblem ? problemId : null, helpId].filter(Boolean).join(' ');
  const look = STEP_LOOK[step];

  return (
    <section aria-labelledby={questionId} className="n3-product__card" tabIndex={-1}>
      <ProductTile icon={look.icon} size="lg" tone={look.tone} />
      <h2 className="n3-product__question" id={questionId}>{STEP_QUESTION[step]}</h2>
      {step === 'name' && <NameStep {...props} describedBy={describedBy} helpId={helpId} invalid={Boolean(questionProblem)} questionId={questionId} />}
      {step === 'price' && <PriceStep {...props} describedBy={describedBy} helpId={helpId} invalid={Boolean(questionProblem)} questionId={questionId} />}
      {step === 'stores' && <StoresStep {...props} describedBy={describedBy} helpId={helpId} questionId={questionId} />}
      {step === 'recipe' && <CostStep {...props} describedBy={describedBy} helpId={helpId} questionId={questionId} subProblem={subProblem} />}
      {step === 'tax' && <TaxStep {...props} describedBy={describedBy} questionId={questionId} subProblem={subProblem} />}
      {questionProblem && <ProductProblem id={problemId}>{questionProblem}</ProductProblem>}
      {step === 'recipe' && props.draft.costMode === 'RECIPE' && <RecipeSub {...props} describedBy={describedBy} questionId={questionId} subProblem={subProblem} />}
      {step === 'recipe' && props.draft.costMode === 'APPROXIMATE' && <ApproximateSub {...props} describedBy={describedBy} questionId={questionId} subProblem={subProblem} />}
      {step === 'tax' && props.draft.tax === 'CUSTOM' && <PercentSub {...props} describedBy={describedBy} questionId={questionId} subProblem={subProblem} />}
    </section>
  );
}

interface PartProps extends StepCardProps { describedBy: string; questionId: string; helpId?: string; invalid?: boolean; subProblem?: string | null }

function enterAdvances(onEnter: () => void) {
  return (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') { event.preventDefault(); onEnter(); }
  };
}

function NameStep({ draft, disabled, onDraft, onEnter, describedBy, helpId, invalid }: PartProps) {
  return (
    <>
      <Input
        aria-describedby={describedBy}
        aria-label="Nombre del producto"
        autoComplete="off"
        className="n3-product__big"
        disabled={disabled}
        enterKeyHint="next"
        invalid={invalid}
        maxLength={PRODUCT_NAME_MAX}
        onChange={(event) => onDraft({ name: event.target.value })}
        onKeyDown={enterAdvances(onEnter)}
        placeholder="Ej. Agua embotellada"
        type="text"
        value={draft.name}
      />
      <ProductHelp id={helpId}>Así lo verás al momento de cobrar.</ProductHelp>
    </>
  );
}

function PriceStep({ draft, disabled, onDraft, onEnter, describedBy, helpId, invalid }: PartProps) {
  return (
    <>
      <Input
        aria-describedby={describedBy}
        aria-label="Precio de venta en pesos"
        autoComplete="off"
        className="n3-product__big n3-product__big--money"
        disabled={disabled}
        enterKeyHint="next"
        inputMode="decimal"
        invalid={invalid}
        onChange={(event) => onDraft({ price: sanitizePrice(event.target.value) })}
        onKeyDown={enterAdvances(onEnter)}
        placeholder="0"
        prefix="$"
        suffix="MXN"
        type="text"
        value={draft.price}
      />
      <ProductHelp id={helpId}>Este será el precio que verá quien cobra.</ProductHelp>
    </>
  );
}

function StoresStep({ draft, disabled, stores, ownerAll, onToggleAll, onToggleStore, describedBy, questionId, helpId }: PartProps) {
  // Para el OWNER, «Todas mis tiendas» incluye las que agregue después: el contrato lo dice así
  // (lista vacía) y es lo que pasaba en el alta de siempre sin marcar ninguna.
  const help = ownerAll && draft.allStores
    ? 'Aparecerá para cobrar en todas tus tiendas, también en las que agregues después.'
    : 'Solo aparecerá para cobrar en las tiendas que elijas.';
  return (
    <>
      <ProductSwitch describedBy={describedBy} disabled={disabled} on={draft.allStores} onToggle={onToggleAll}>Todas mis tiendas</ProductSwitch>
      {stores.length > 0 && (
        <fieldset aria-describedby={describedBy} aria-labelledby={questionId} className="n3-product__choices">
          <div className="n3-product__options">
            {stores.map((store) => (
              <ProductOption
                checked={isStoreChosen(draft, store.id)}
                disabled={disabled}
                hint={store.isActive ? 'Tienda' : 'Tienda inactiva'}
                icon="stores"
                key={store.id}
                name="product-stores"
                onChange={() => onToggleStore(store.id)}
                title={store.name}
                tone="info"
                type="checkbox"
                value={store.id}
              />
            ))}
          </div>
        </fieldset>
      )}
      <ProductHelp id={helpId}>{help}</ProductHelp>
    </>
  );
}

/** P-071: «¿Cómo quieres llevar el costo?» — tres respuestas, una sola activa. */
function CostStep({ draft, disabled, onDraft, describedBy, questionId, helpId }: PartProps) {
  return (
    <>
      <fieldset aria-describedby={describedBy} aria-labelledby={questionId} className="n3-product__choices">
        <div className="n3-product__options">
          {COST_OPTIONS.map((option) => (
            <ProductOption
              checked={draft.costMode === option.value}
              disabled={disabled}
              hint={option.hint}
              icon={COST_LOOK[option.value].icon}
              key={option.value}
              name="product-cost-mode"
              onChange={() => onDraft({ costMode: option.value })}
              title={option.label}
              tone={COST_LOOK[option.value].tone}
              type="radio"
              value={option.value}
            />
          ))}
        </div>
      </fieldset>
      <ProductHelp id={helpId}>Puedes cambiarlo después desde el catálogo; solo afecta ventas futuras.</ProductHelp>
    </>
  );
}

/** P-071: el costo aproximado por unidad, con el campo grande del precio. Sin margen ni ganancia aquí. */
function ApproximateSub({ draft, disabled, onDraft, onEnter, subProblem }: PartProps) {
  const titleId = 'product-approximate-title';
  const problemId = 'product-problem-approximate';
  return (
    <div className="n3-product__sub">
      <h3 className="n3-product__sub-title" id={titleId}>Costo aproximado por unidad</h3>
      <Input
        aria-describedby={subProblem ? problemId : undefined}
        aria-label="Costo aproximado por unidad"
        autoComplete="off"
        className="n3-product__big n3-product__big--money"
        disabled={disabled}
        enterKeyHint="next"
        inputMode="decimal"
        invalid={Boolean(subProblem)}
        onChange={(event) => onDraft({ approximateCost: sanitizePrice(event.target.value) })}
        onKeyDown={enterAdvances(onEnter)}
        placeholder="0"
        prefix="$"
        suffix="MXN"
        type="text"
        value={draft.approximateCost}
      />
      <ProductHelp fine>Lo que te cuesta una pieza, más o menos. No se descuenta inventario.</ProductHelp>
      {subProblem && <ProductProblem id={problemId}>{subProblem}</ProductProblem>}
    </div>
  );
}

function RecipeSub({ draft, disabled, onDraft, recipes, recipeQuery, onRecipeQuery, onRetryRecipes, subProblem }: PartProps) {
  const titleId = 'product-recipe-pick';
  const problemId = 'product-problem-recipe-pick';
  // El problema («Elige una receta.») va con el grupo de recetas y también con el buscador, que queda
  // fuera del grupo y es lo primero que recibe el foco.
  const problemRef = subProblem ? problemId : undefined;
  let body: ReactNode;
  if (recipes.status === 'loading') {
    body = <p className="n3-product__note" role="status">Cargando tus recetas…</p>;
  } else if (recipes.status === 'error') {
    body = (
      <div className="n3-product__note" role="alert">
        <span>No pudimos traer tus recetas.</span>
        <Button onClick={onRetryRecipes} variant="secondary">Reintentar</Button>
      </div>
    );
  } else if (recipes.choices.length === 0) {
    body = <p className="n3-product__note">Todavía no tienes recetas activas.</p>;
  } else {
    const shown = filterRecipes(recipes.choices, recipeQuery);
    body = (
      <>
        <Input
          aria-describedby={problemRef}
          aria-label="Buscar receta"
          autoComplete="off"
          className="n3-product__search"
          disabled={disabled}
          onChange={(event) => onRecipeQuery(event.target.value)}
          placeholder="Buscar receta"
          prefix={<Icon name="search" size={18} />}
          type="search"
          value={recipeQuery}
        />
        {shown.length === 0
          ? <p className="n3-product__note" role="status">No encontramos esa receta.</p>
          : (
            <fieldset aria-describedby={problemRef} aria-labelledby={titleId} className="n3-product__choices">
              <div className="n3-product__items">
                {shown.map((choice) => {
                  const checked = draft.recipeId === choice.id;
                  return (
                    <label className={cx('n3-product__item', checked && 'n3-product__item--on', !choice.selectable && 'n3-product__item--off')} key={choice.id}>
                      <input
                        checked={checked}
                        className="n3-product__option-input"
                        disabled={disabled || !choice.selectable}
                        name="product-recipe"
                        onChange={() => onDraft({ recipeId: choice.id })}
                        type="radio"
                        value={choice.id}
                      />
                      <span className="n3-product__item-box">
                        <span className="n3-product__item-text">
                          <span className="n3-product__item-name">{choice.name}</span>
                          {choice.note && <span className="n3-product__item-note">{choice.note}</span>}
                        </span>
                        <span aria-hidden="true" className="n3-product__mark">{checked && <Icon name="check" size={14} />}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          )}
      </>
    );
  }
  return (
    <div className="n3-product__sub">
      <h3 className="n3-product__sub-title" id={titleId}>¿Cuál receta?</h3>
      {body}
      <ProductHelp fine>Solo puedes elegir recetas que ya existen.</ProductHelp>
      {subProblem && <ProductProblem id={problemId}>{subProblem}</ProductProblem>}
    </div>
  );
}

function TaxStep({ draft, disabled, onDraft, describedBy, questionId }: PartProps) {
  return (
    <fieldset aria-describedby={describedBy || undefined} aria-labelledby={questionId} className="n3-product__choices">
      <div className="n3-product__options">
        {TAX_OPTIONS.map((option) => (
          <ProductOption
            checked={draft.tax === option.value}
            disabled={disabled}
            hint={taxHint(option.value, draft.price)}
            icon={TAX_LOOK[option.value].icon}
            key={option.value}
            name="product-tax"
            onChange={() => onDraft({ tax: option.value })}
            title={option.label}
            tone={TAX_LOOK[option.value].tone}
            type="radio"
            value={option.value}
          />
        ))}
      </div>
    </fieldset>
  );
}

function PercentSub({ draft, disabled, onDraft, onEnter, subProblem }: PartProps) {
  const titleId = 'product-percent-title';
  const problemId = 'product-problem-percent';
  return (
    <div className="n3-product__sub">
      <h3 className="n3-product__sub-title" id={titleId}>¿Qué porcentaje?</h3>
      <Input
        aria-describedby={subProblem ? problemId : undefined}
        aria-label="Porcentaje de impuesto"
        autoComplete="off"
        className="n3-product__big n3-product__big--percent"
        disabled={disabled}
        enterKeyHint="done"
        inputMode="decimal"
        invalid={Boolean(subProblem)}
        onChange={(event) => onDraft({ taxPercent: sanitizePercent(event.target.value) })}
        onKeyDown={enterAdvances(onEnter)}
        placeholder="Ej. 8"
        suffix="%"
        type="text"
        value={draft.taxPercent}
      />
      {subProblem && <ProductProblem id={problemId}>{subProblem}</ProductProblem>}
    </div>
  );
}
